import React from 'react';
import { WidgetProps } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import './styles/CustomRangeStyles.css';

const CustomRangeWidget: React.FC<WidgetProps> = ({
  schema,
  value,
  label,
  required,
  onChange,
  options,
  disabled,
  readonly,
  rawErrors,
}) => {
  const { t } = useTranslation();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    // Update the CSS variable for the selected value
    e.target.style.setProperty(
      '--value',
      `${((e.target.valueAsNumber - Number(e.target.min)) / (Number(e.target.max) - Number(e.target.min))) * 100}%`,
    );
  };

  const min = typeof schema.minimum === 'number' ? schema.minimum : 0;
  const max = typeof schema.maximum === 'number' ? schema.maximum : 100;
  // The ui option wins, then the schema's own multipleOf, so a 0–1 range in
  // tenths keeps its tenths.
  const hasError = !!options.hasError || !!rawErrors?.length;
  const step =
    typeof options.step === 'number'
      ? options.step
      : typeof schema.multipleOf === 'number'
        ? schema.multipleOf
        : 1;
  // A field with no value yet sits at the minimum, so the track still draws.
  const current =
    Number.isFinite(Number(value)) && value !== '' && value != null
      ? Number(value)
      : min;

  return (
    <div className="flex flex-col">
      <div>
        <span className="mt-1 text-center font-normal text-ui-text-primary-text">
          {t('Selected: {{value}}', { value: current })}
        </span>
        <div className="flex flex-row items-center">
          <span className="mr-2 font-normal text-ui-text-primary-text">
            {min}
          </span>
          <input
            type="range"
            value={current}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            disabled={disabled || readonly}
            aria-invalid={hasError || undefined}
            className="range-thumb mt-2 h-2 w-full cursor-pointer rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
            style={
              {
                '--value': `${max > min ? ((current - min) / (max - min)) * 100 : 0}%`,
              } as React.CSSProperties
            }
          />
          <span className="ml-2 font-normal text-ui-text-primary-text">
            {max}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CustomRangeWidget;
